import type { Issue, Paginated } from '../types';

export const issueSorts = ['id_desc', 'id_asc', 'title_asc', 'title_desc', 'company_asc', 'company_desc', 'status_asc', 'status_desc', 'assignee_asc', 'assignee_desc', 'updated_asc', 'updated_desc', 'next_action_asc', 'next_action_desc'] as const;
export type IssueSort = typeof issueSorts[number];
export function parseIssueSort(value?: string): IssueSort {
  return issueSorts.includes(value as IssueSort) ? value as IssueSort : 'id_desc';
}

/** Complete bounded snapshot for client-side GLOBAL sort, never a partial page. */
export async function loadIssueSnapshot(list: (page: number, perPage: number) => Promise<Paginated<Issue>>, limit = 2000): Promise<Issue[]> {
  const first = await list(1, 100);
  if (first.count > limit) throw new Error(`More than ${limit.toLocaleString('en-IN')} matching issues. Narrow the filters to sort the full result.`);
  const rows = [...first.results];
  // Honour the server's actual page size rather than assuming the requested size.
  if (first.count && first.per_page < 1) throw new Error('Invalid issue pagination. Retry loading the list.');
  for (let page = 2; page <= Math.ceil(first.count / first.per_page); page++) {
    const next = await list(page, first.per_page);
    if (next.count !== first.count || next.per_page !== first.per_page) throw new Error('Issues changed while sorting. Retry for a fresh list.');
    rows.push(...next.results);
  }
  if (new Set(rows.map(row => row.id)).size !== first.count || rows.length !== first.count) throw new Error('Issues changed while sorting. Retry for a fresh list.');
  return rows;
}

export function sortIssues(rows: readonly Issue[], sort: IssueSort, assigneeName: (id: number | null) => string = id => id ? String(id) : ''): Issue[] {
  const descending = sort.endsWith('_desc');
  const field = sort.slice(0, sort.lastIndexOf('_'));
  const value = (row: Issue): string | number | null => {
    switch (field) {
      case 'title': return row.title;
      case 'company': return row.company_name || null;
      case 'status': return row.status;
      case 'assignee': return row.assignee_user_id ? assigneeName(row.assignee_user_id) : null;
      case 'updated': return Date.parse(row.updated_at) || null;
      case 'next_action': return row.next_action_date || null;
      default: return row.id;
    }
  };
  return [...rows].sort((a, b) => {
    const left = value(a), right = value(b);
    if (left === null || right === null) return left === right ? a.id - b.id : left === null ? 1 : -1;
    const comparison = typeof left === 'number' && typeof right === 'number' ? left - right : String(left).localeCompare(String(right), 'en-IN', { numeric: true, sensitivity: 'base' });
    return (descending ? -comparison : comparison) || a.id - b.id;
  });
}

export interface BulkIssueResult { succeeded: number[]; failed: { id: number; message: string }[] }
/** Per-record writes: a failure never claims rollback of preceding successes. */
export async function updateIssuesIndividually(rows: readonly (Pick<Issue, 'id' | 'vertical_id'> & Partial<Pick<Issue, 'status'>>)[], body: Record<string, unknown>, update: (id: number, body: Record<string, unknown>) => Promise<unknown>, errorMessage: (error: unknown) => string): Promise<BulkIssueResult> {
  const result: BulkIssueResult = { succeeded: [], failed: [] };
  for (const row of rows) {
    const request: Record<string, unknown> = { ...body, vertical_id: row.vertical_id };
    // A mixed selection may contain resolved records. Do not overwrite their
    // existing technician/CAPA evidence when resolving the remaining records.
    if (body.status === 'resolved' && row.status === 'resolved') {
      for (const field of ['resolving_technician_name', 'capa_root_cause', 'capa_corrective_action', 'capa_preventive_action']) delete request[field];
    }
    try { await update(row.id, request); result.succeeded.push(row.id); }
    catch (error) { result.failed.push({ id: row.id, message: errorMessage(error) }); }
  }
  return result;
}
